import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { RESET } from "jotai/utils";
import {
  TournamentParticipantList,
  Tournament,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./GameResultsPage.scss";
import GhostButton from "../../components/dashboard-page/GhostButton/GhostButton";
import BulbIcon from "../../components/icons/LightBulbIcon";
import Tip from "../../components/game-play/Tip/Tip";
import InfoIcon2 from "../../components/icons/InfoIcon2";
import useTournamentParticipantsQuery from "../../react-query/queries/use-tournament-participants";
import {
  duelEntryAtomWithStorage,
  duelOpponentAtomWithStorage,
} from "../../jotai/atoms/tournament";
import usePlatformDuelEntryMutation from "../../react-query/mutations/use-platform-duel-entry";
import {
  GameFlowState,
  HttpStatusCodes,
  GameOutcome as Outcome,
  ROUTES,
  TOURNAMENT_TO_GAME_TYPE_MAP,
} from "../../utils/constants";
import Result from "../../components/game-results-page/Result/Result";

import { gameFlowStateAtomWithStorage } from "../../jotai/atoms/games";
import GatheringInfo from "../../components/game-play/GatheringInfo/GatheringInfo";
import UnableToPerformActionModal, {
  GameFailureActions,
} from "../../components/game-play/UnableToPerformActionModal/UnableToPerformActionModal";
import { CustomSpinner } from "../../components/CustomSpinner/CustomSpinner";
import ImageCloseIcon from "../../images/close-icon-white.png";
import PrimaryActionButton from "../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import useAuth from "../../hooks/useAuth";
import useAnalytics from "../../hooks/useAnalytics";
import PlayIconFlilled from "../../components/icons/PlayIconFlilled";
import useGameplayResultsQuery from "../../react-query/queries/use-gameplay-results";
import { QUERY_KEYS } from "../../react-query/constants";

interface GameResultsPageProps {
  tournamentDetails?: Tournament;
}

function GameResultsPage({ tournamentDetails }: GameResultsPageProps) {
  const { t } = useTranslation("games");
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { tournamentId } = useParams();
  const [duelOpponent, setDuelOpponent] = useAtom(duelOpponentAtomWithStorage);
  const [duelEntry] = useAtom(duelEntryAtomWithStorage);
  const [gameFlowState] = useAtom(gameFlowStateAtomWithStorage);

  useEffect(() => {
    const storedTournamentId = duelEntry?.tournament?.id;
    if (
      storedTournamentId &&
      tournamentId &&
      storedTournamentId !== tournamentId &&
      duelOpponent
    ) {
      setDuelOpponent(RESET);
    }
  }, [tournamentId, duelEntry, duelOpponent, setDuelOpponent]);
  const { userProfile } = useAuth();
  const { sendCompleteGameEvent } = useAnalytics();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const returnToRoute =
    typeof location.state?.returnTo === "string"
      ? location.state.returnTo
      : undefined;

  const {
    data: tournamentParticipants,
    isLoading: isLoadingParticipants,
    isFetching: isFetchingParticipants,
    error: tournamentParticipantsError,
  } = useTournamentParticipantsQuery({
    tournamentId: tournamentId || "",
    enabled: true,
    refetchInterval: 10 * 1000,
    retry: 1,
  });

  const [userOutcome, setUserOutcome] = useState<Outcome>();
  const [opponentOutcome, setOpponentOutcome] = useState<Outcome>();
  const loggedInUsername = userProfile?.username;
  const { data: gameplayResults } = useGameplayResultsQuery({ limit: 5 });
  const {
    playBattleGame,
    mutation: { isPending: isBattleGameEntryPending },
  } = usePlatformDuelEntryMutation();

  const activeGameId = tournamentDetails?.game.id;

  const userInfo = tournamentParticipants?.records.find(
    (participant) => participant.username === loggedInUsername
  );

  const opponentInfo = tournamentParticipants?.records.find(
    (participant) => participant.username !== loggedInUsername
  );

  const evaluateDuelOutcomes = useCallback(
    (participants?: TournamentParticipantList) => {
      const hasBeenMatched = participants?.records.length === 2;

      if (!duelOpponent && !hasBeenMatched) {
        setUserOutcome(Outcome.WAITING_TO_BE_MATCHED);
        setOpponentOutcome(undefined);
        return;
      }
      if (duelOpponent && !hasBeenMatched && !isLoadingParticipants) {
        setUserOutcome(Outcome.OPPONENT_CANCELLED);
        setOpponentOutcome(undefined);
        return;
      }
      if (userInfo?.score != null && opponentInfo?.score == null) {
        setUserOutcome(Outcome.AWAITING_OPPONENT_SCORE);
        setOpponentOutcome(Outcome.AWAITING_SCORE);
        return;
      }
      if (userInfo?.score === undefined || opponentInfo?.score === undefined) {
        return;
      }
      if (userInfo.score > opponentInfo.score) {
        setUserOutcome(Outcome.WIN);
        setOpponentOutcome(Outcome.LOSS);
        return;
      }
      if (userInfo.score < opponentInfo.score) {
        setUserOutcome(Outcome.LOSS);
        setOpponentOutcome(Outcome.WIN);
        return;
      }
      if (userInfo.score === opponentInfo.score) {
        setUserOutcome(Outcome.DRAW);
        setOpponentOutcome(Outcome.DRAW);
        return;
      }
    },
    [duelOpponent, isLoadingParticipants, userInfo, opponentInfo]
  );

  let playerPosition: number | null;
  switch (userOutcome) {
    case Outcome.DRAW:
      playerPosition = 0;
      break;
    case Outcome.WIN:
      playerPosition = 1;
      break;
    case Outcome.LOSS:
      playerPosition = 2;
      break;
    default:
      playerPosition = null;
  }

  let headingText: React.ReactNode;
  switch (userOutcome) {
    case Outcome.WIN:
      headingText = t("game_results_page.you_won");
      break;
    case Outcome.DRAW:
      headingText = t("game_results_page.it_is_a_tie");
      break;
    case Outcome.LOSS:
      headingText = t("game_results_page.second_place");
      break;
    case Outcome.AWAITING_OPPONENT_SCORE:
      headingText = t("game_results_page.waiting_for_your_opponents_score");
      break;
    case Outcome.OPPONENT_CANCELLED:
      headingText = t("cancelled");
      break;
    case Outcome.WAITING_TO_BE_MATCHED:
      headingText = (
        <>
          {t("searching_for_opponent")} <br />
          <span style={{ color: "#ffac00" }}>{t("within_skill_level")}</span>
        </>
      );
      break;
    default:
      headingText = "";
  }

  let tipText: string;
  let tipIcon: JSX.Element;
  if (userOutcome === Outcome.AWAITING_OPPONENT_SCORE) {
    tipText = t("game_results_page.we_will_notify_when_score_comes", {
      username: opponentInfo?.username,
    });
    tipIcon = <BulbIcon fill="white" />;
  } else if (
    userOutcome === Outcome.OPPONENT_CANCELLED ||
    userOutcome === Outcome.WAITING_TO_BE_MATCHED
  ) {
    tipText = t("game_results_page.matching_takes_anywhere_key");
    tipIcon = <InfoIcon2 fill="white" width={24} height={24} />;
  } else {
    tipText = "";
    tipIcon = <></>;
  }

  const hasGameFailed =
    (!isFetchingParticipants &&
      userInfo &&
      typeof userInfo.score !== "number") ||
    (gameFlowState === GameFlowState.VIEWING_RESULTS_FOR_FAILED_GAME &&
      typeof userInfo?.score !== "number");

  const hasFetchError =
    !!tournamentParticipantsError &&
    !(
      tournamentParticipantsError instanceof AxiosError &&
      tournamentParticipantsError.response?.status === HttpStatusCodes.FORBIDDEN
    );

  const playAnotherGame = useCallback(() => {
    if (!activeGameId) return;
    setDuelOpponent(RESET);
    playBattleGame(activeGameId);
  }, [activeGameId, playBattleGame, setDuelOpponent]);

  const resultEl = useMemo(
    () => (
      <Result
        duelOpponent={duelOpponent}
        headingText={headingText}
        opponentInfo={opponentInfo}
        opponentOutcome={opponentOutcome}
        tournamentDetails={tournamentDetails}
        userInfo={userInfo}
        userOutcome={userOutcome}
        hasGameFailed={hasGameFailed}
        gameplayResults={gameplayResults}
      />
    ),
    [
      duelOpponent,
      gameplayResults,
      hasGameFailed,
      headingText,
      opponentInfo,
      opponentOutcome,
      tournamentDetails,
      userInfo,
      userOutcome,
    ]
  );

  useEffect(() => {
    evaluateDuelOutcomes(tournamentParticipants);
  }, [evaluateDuelOutcomes, tournamentParticipants]);

  useEffect(() => {
    if (
      !tournamentParticipantsError ||
      !(tournamentParticipantsError instanceof AxiosError)
    ) {
      return;
    }

    if (
      tournamentParticipantsError.response?.status === HttpStatusCodes.FORBIDDEN
    ) {
      navigate(`${ROUTES.GAMES.ROOT}`);
    }
  }, [navigate, tournamentParticipantsError]);

  useEffect(() => {
    if (
      userOutcome !== Outcome.WIN &&
      userOutcome !== Outcome.LOSS &&
      userOutcome !== Outcome.DRAW
    )
      return;
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.ACTIVITY.BASE],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.TOURNAMENT.GAMEPLAY_RESULTS],
    });
  }, [userOutcome, queryClient]);

  useEffect(() => {
    if (!userOutcome || !tournamentDetails) return;
    const { stakeAmount, type, game, rewardAmount } = tournamentDetails;
    sendCompleteGameEvent({
      entry_fee: stakeAmount,
      game_name: game.name,
      game_mode: TOURNAMENT_TO_GAME_TYPE_MAP[type],
      player_position: playerPosition,
      hpr_reward: rewardAmount,
    });
  }, [userOutcome, tournamentDetails, playerPosition, sendCompleteGameEvent]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsInitialLoad(false);
    }, 1500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div
      className={`game-results-page MobileOnly position-relative d-flex flex-column ${
        !isLoadingParticipants ? "justify-content-between" : ""
      }`}
    >
      {isLoadingParticipants || isInitialLoad ? (
        <GatheringInfo
          duelOpponent={duelOpponent}
          tournamentDetails={tournamentDetails}
        />
      ) : hasFetchError && !tournamentParticipants ? (
        <UnableToPerformActionModal
          show
          tournament={tournamentDetails}
          action={GameFailureActions.SUBMIT_SCORES}
        />
      ) : (
        <>
          {!hasGameFailed && (
            <GhostButton
              className="rounded-btn"
              style={{ left: 16 }}
              aria-label="close"
              onClick={() => {
                if (returnToRoute) {
                  navigate(returnToRoute);
                } else if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(`${ROUTES.GAMES.ROOT}`);
                }
              }}
            >
              <Image src={ImageCloseIcon} alt="" height={14} width={14} />
            </GhostButton>
          )}
          {resultEl}
          {!hasGameFailed && (
            <div className="px-3 pb-4 results-footer">
              {tipText && (
                <Tip
                  icon={tipIcon}
                  className="mb-3"
                  style={{ textAlign: "left" }}
                >
                  {tipText}
                </Tip>
              )}
              {userOutcome !== Outcome.WAITING_TO_BE_MATCHED && (
                <PrimaryActionButton
                  buttonClassName="w-100 gap-2 d-flex align-items-center justify-content-center"
                  onClick={playAnotherGame}
                  disabled={isBattleGameEntryPending}
                  leftImage={
                    isBattleGameEntryPending ? undefined : (
                      <PlayIconFlilled fill="white" />
                    )
                  }
                  text={
                    isBattleGameEntryPending ? (
                      <CustomSpinner size="sm" />
                    ) : (
                      t("game_results_page.play_again")
                    )
                  }
                />
              )}
            </div>
          )}
          {hasGameFailed && (
            <UnableToPerformActionModal
              show={hasGameFailed}
              tournament={tournamentDetails}
              action={
                gameFlowState === GameFlowState.VIEWING_RESULTS_FOR_FAILED_GAME
                  ? GameFailureActions.LAUNCH_GAME
                  : GameFailureActions.SUBMIT_SCORES
              }
            />
          )}
        </>
      )}
    </div>
  );
}

export default GameResultsPage;
